/**
 * The apps an online appointment's meeting link can come from.
 *
 * One list, in one place: the Integrate apps card on My brand → Configuration
 * draws a row per entry, and the Schedule dialog shows the same marks in the
 * strip that asks for one to be connected. Two copies of "Google meet, Zoom"
 * would drift the moment a third app is added.
 */

import { API_ENVIRONMENT, type ApiEnvironment } from "../../lib/api";
import type { MeetingProviderSlug } from "./types/appointments.types";

export type MeetingAppId = "google-meet" | "zoom";

/**
 * How the backend names each of them in a path — its `slug`.
 *
 * Here beside the list rather than in the hook that reads the settings,
 * because two parts of the app need it for two different reasons: the hook
 * puts it in `/meeting-providers/{slug}/connect/` and matches it against the
 * `providers` the settings come back with, and the callback page puts it in
 * the call that finishes the handshake. Two copies would be two places to
 * forget the next app.
 *
 * `MeetingProviderSlug` is the wire's own type for these words — imported
 * rather than restated so a slug the backend does not know cannot be written
 * here.
 */
export const APP_SLUGS: Record<MeetingAppId, MeetingProviderSlug> = {
  "google-meet": "google",
  zoom: "zoom",
};

export interface MeetingApp {
  id: MeetingAppId;
  /** As the practice reads it — sentence case, the way the design writes it. */
  name: string;
  /** What connecting it buys, in one line. */
  description: string;
  /**
   * False while there is nothing to connect to yet: the row is still drawn,
   * with "Coming soon" where its button would be. Drawn rather than left out
   * because the card is also a statement of what is on the way, and an app
   * that appears from nowhere in a later release reads as one nobody was
   * told about.
   *
   * Zoom is false while its app is still in Zoom's review. Everything that
   * connects it is written and left in place — the slug, the hook, the
   * callback page — because none of it is reachable with no button to press,
   * and the day the review clears this is one word. Google Meet ships to
   * production meanwhile, which is the whole point of the flag: the two apps
   * do not have to go out together.
   */
  available: boolean;
  /**
   * Who gets the real button anyway, while `available` is false — the one
   * account per backend that the flow is being tried from.
   *
   * Keyed by the backend this bundle was built against, because the account
   * is not the same one on each: an unpublished Zoom app only authorises the
   * accounts added to it on Zoom's side, so the address that can finish the
   * handshake against production is not the address that can against
   * staging, and a single list would hand half the pilots a button that ends
   * in Zoom's own error.
   *
   * Everybody else reads "Coming soon", which is what the whole workspace
   * read before and what they will read until the review clears. Then this
   * goes and `available` becomes true — one deletion, not an unpicking.
   *
   * It hides a button; it does not lock a door. The addresses ship inside
   * the bundle and `/meeting-providers/zoom/connect/` stays reachable to
   * anyone who looks, so this is a release gate and not a permission. If it
   * ever has to be enforced, that belongs on the settings the backend sends.
   */
  pilotEmails?: Partial<Record<ApiEnvironment, readonly string[]>>;
}

export const MEETING_APPS: MeetingApp[] = [
  {
    id: "google-meet",
    name: "Google meet",
    description:
      "Connect your Google meet account with Naurist & create appointments automatically.",
    available: true,
  },
  {
    id: "zoom",
    name: "Zoom",
    description:
      "Connect your Zoom account with Naurist & create appointments automatically.",
    available: false,
    pilotEmails: {
      production: ["dev+clerk_test@naurist.com"],
      staging: ["sameer@openui.in"],
    },
  },
];

/**
 * Whether this app can be connected by the person reading the screen.
 *
 * The question the card and the Schedule dialog both ask, in one place,
 * because they have to answer it the same way: a logo in the dialog's strip
 * is a promise that pressing through connects the app, and a row that then
 * said "Coming soon" would be taking it back.
 *
 * `email` is the signed-in address — our own record of it where there is one,
 * Clerk's otherwise, which is what `useSignedInUser` already settles. Empty
 * while Clerk is still restoring the session, and empty is not a match:
 * callers wait for `isLoaded` rather than drawing a row they will have to
 * redraw.
 */
export const availableTo = (app: MeetingApp, email: string): boolean => {
  if (app.available) return true;

  const address = email.trim().toLowerCase();
  if (!address) return false;

  return !!app.pilotEmails?.[API_ENVIRONMENT]?.includes(address);
};
